/**
 * Shared date formatting (IR-116 spec §4) — one util, the vue-i18n app locale
 * (never the browser locale), `'—'` for empty input, raw-string fallback when a
 * value can't be parsed. Promoted from `ContinuityPanel`'s local `fmtDate`
 * (the only tab that formatted dates at all before this).
 */
import i18n from '@/plugins/i18n'
import type { DatePrecision } from '@/models/registry'

function currentLocale(): string {
  const l = i18n.global.locale
  return typeof l === 'string' ? l : l.value
}

function parseDate(iso: string): Date | null {
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Date-only, e.g. `01/03/2026` (fr) / `3/1/2026` (en). `'—'` when empty. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = parseDate(iso)
    if (!d) return iso
    return d.toLocaleDateString(currentLocale())
  } catch {
    return iso
  }
}

/** Date with a spelled month, e.g. `3 sept. 2026` (fr) / `Sep 3, 2026` (en).
 *  MYAC-1160 review pass (NOW-4): `fmtDate`'s numeric form rendered
 *  « 9/3/2026 » on the Suggested status line — a date a French reader parses
 *  as 9 March. `'—'` when empty, raw string when unparseable, same contract
 *  as `fmtDate`. */
export function fmtDateMedium(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = parseDate(iso)
    if (!d) return iso
    return d.toLocaleDateString(currentLocale(), { dateStyle: 'medium' })
  } catch {
    return iso
  }
}

/** Date + time, short style, app locale. `'—'` when empty. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = parseDate(iso)
    if (!d) return iso
    return d.toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}

/** A Firestore `Timestamp`, or the `Date` a test or a cached read hands back. */
interface DateLike {
  toDate?: () => Date
}

/**
 * A view row's timestamp as an ISO string, ready for `fmtDate` / `fmtDateMedium`
 * — `''` when the row carries none, which is legitimate for rows written before
 * a field existed.
 *
 * Lives here rather than beside one feature because every view row that shows a
 * date needs it: Firestore hands back a `Timestamp`, the composables that read
 * views do not transform it, and `@Transform(anyToDate)` only applies to the
 * class-based models — view rows are plain objects.
 */
export function isoFromTimestamp(value: unknown): string {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString()
  }
  const ts = value as DateLike | null | undefined
  if (typeof ts?.toDate !== 'function') return ''
  const d = ts.toDate()
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : ''
}

/** The day format registry revision dates have always had on screen
 *  (`01 févr. 2026` / `Feb 01, 2026`). */
export const REVISION_DAY_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
}

/**
 * Intl options for a registry revision date at its precision (D75): a `month`
 * date reads month + year (`févr. 2026` / `Feb 2026`), a `year` date the year
 * alone, so a client never reads a day nobody read on the source. `day` (and
 * `unknown`, a precision this build does not know) keep `dayOptions` exactly as
 * the caller formatted them before.
 *
 * A coarse date is stored as UTC midnight of its first day (YYYY-MM-01,
 * YYYY-01-01): it is read in UTC, or a reader west of Greenwich would see the
 * previous month (or year).
 */
export function revisionDateFormatOptions(
  precision: DatePrecision,
  dayOptions: Intl.DateTimeFormatOptions = REVISION_DAY_FORMAT,
): Intl.DateTimeFormatOptions {
  if (precision === 'month') return { year: 'numeric', month: 'short', timeZone: 'UTC' }
  if (precision === 'year') return { year: 'numeric', timeZone: 'UTC' }
  return dayOptions
}

/**
 * A registry revision date in ISO form at its precision (D75), for the PDF
 * report and the spreadsheet: `2026-02-12` (day), `2026-02` (month), `2026`
 * (year). UTC, like the day form the PDF always printed.
 */
export function fmtRevisionDateIso(date: Date, precision: DatePrecision): string {
  const iso = date.toISOString()
  if (precision === 'month') return iso.slice(0, 7)
  if (precision === 'year') return iso.slice(0, 4)
  return iso.slice(0, 10)
}

/** Formats a registry date at its precision (D75); no precision = `day`. */
export type PrecisionDateFormat = (date: Date, precision?: DatePrecision) => string

/**
 * A `PrecisionDateFormat` over any Intl-style formatter (vue-i18n's `d`, an
 * `Intl.DateTimeFormat`): the day keeps `dayOptions`, a `month` / `year` date
 * gets `revisionDateFormatOptions`. The one way the v3 report's page, PDF and
 * spreadsheet build their date formatter, so none of them prints a day nobody
 * read.
 */
export function precisionDateFormat(
  format: (date: Date, options: Intl.DateTimeFormatOptions) => string,
  dayOptions: Intl.DateTimeFormatOptions = REVISION_DAY_FORMAT,
): PrecisionDateFormat {
  return (date, precision = 'day') => format(date, revisionDateFormatOptions(precision, dayOptions))
}

/** The calendar year of a registry date at its precision: a `month` / `year`
 *  date is UTC midnight of its first day, read in UTC (`revisionDateFormatOptions`). */
export function revisionDateYear(date: Date, precision: DatePrecision): number {
  return precision === 'month' || precision === 'year' ? date.getUTCFullYear() : date.getFullYear()
}
